import { createFileRoute, useRouter } from '@tanstack/react-router'
// The SCHEMA is what this page needs: it is the public report form's validator, and this route IS that
// form. It ships zod either way, so importing the schema through the barrel costs it nothing extra — it is
// on FORM_ROUTES in vite.config.ts for the same reason.
// oxlint-disable-next-line shared-barrel/no-barrel-values -- this route is the form; see above.
import { parsePublicAnswers, publicReportFormSchema, type PublicReportForm } from '@/lib/form-schemas'
import { useAppForm } from '@/lib/use-app-form'
import { AuthPending } from './-components/app-layout-skeleton'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

export const Route = createFileRoute('/_app/beta/generate')({
  component: RouteComponent,
  pendingComponent: AuthPending,
})

function RouteComponent() {
  // Annotated (not inferred) so `name` stays an OPTIONAL key, matching the schema: zod 4.4 treats a
  // property that merely accepts `undefined` as required, so the schema uses `.optional()` and an
  // inferred-from-literal defaultValues would make the key required and fail to match the validator.
  const defaultValues: PublicReportForm = { name: '', answers: '' }
  const router = useRouter()

  const form = useAppForm({
    validators: { onChange: publicReportFormSchema },
    defaultValues,
    onSubmit: ({ value }) => {
      // The schema check above already proved the JSON parses into the three answer sets, so a null
      // here cannot happen; guard rather than assert so a stale form state fails closed.
      const answers = parsePublicAnswers(value.answers)
      if (!answers) return
      // The profile goes into the URL in the COMPACT answers form (`shared/public-answers`): three
      // 36-character strings rather than 108 tuples, which is what keeps the report page's search state,
      // query key and SSR hydration payload small. Built through the router so the search serialisation
      // is the root schema's own: a hand-rolled `/beta/report?profiles=` URL names a route that does not
      // exist and a search key the root schema never reads.
      const { href } = router.buildLocation({
        to: '/report',
        search: (prev) => ({
          ...prev,
          first: { type: 'public', name: value.name || undefined, ...answers },
          selected: 'first',
        }),
      })
      window.open(href, '_blank')?.focus()
    },
  })
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        form.handleSubmit()
      }}
      className="mx-auto flex max-w-96 flex-col gap-4 p-6"
    >
      <form.AppField name="name" children={(field) => <field.TextField label="name" />} />
      <form.AppField name="answers" children={(field) => <field.TextField label="answers" />} />
      <ButtonPrimitive type="submit">generate</ButtonPrimitive>
    </form>
  )
}
